import React, { useEffect } from 'react';
import { Text } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import { prefetchParty } from '../../api/hooks/useParty';
import { colors } from '../../styles/colors';
import { readableListTitleTextWrap } from '../../styles/textWrap';
import useInteraction from '../../hooks/useInteraction';
import { TextVariant } from '../../styles/typography';
import { thousandsSeparators } from '../../utils/format-utils';
import { useDictionary } from '../../hooks/useDictionary';
import Anchor from '../Anchor/Anchor';
import Mark from '../Mark/Mark';
import PartyElectionBadges from './PartyElectionBadges';
import {
  getElectionBadges,
  getPartyExpiryDateAriaLabel,
  getPartyHref,
  getPartyLinkAriaLabel,
  getPartyVoteAriaLabel,
  getVisibleExpiryDates,
} from './partyListDisplay';
import type { PartyListParty } from './partyListModel';

type PartyListItemProps = {
  party: PartyListParty;
  partyDetailsUrl: string;
  isFirstItem?: boolean;
  className?: string;
  highlight?: string;
};

const headlineStyles = {
  color: colors.base.Text.Secondary,
  marginBottom: 0,
  display: ['block', 'block', 'none'],

  // TODO: grid has different breakpoints than css ?
};

function PartyListItem({
  party,
  partyDetailsUrl,
  isFirstItem,
  className,
  highlight,
}: PartyListItemProps) {
  const [ref, { focus, hover, active: touching }] = useInteraction();
  const { name, id, voteCount = 0, voteCount2, type } = party;
  const { t } = useDictionary();

  useEffect(() => {
    if (touching) prefetchParty(id);
    else if (hover || focus) {
      const timeoutId = setTimeout(() => {
        prefetchParty(id);
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [hover, focus, touching, id]);

  const electionTypeBadges = getElectionBadges(type, t);
  const visibleExpiryDates = getVisibleExpiryDates(party);
  const voteAriaLabel = getPartyVoteAriaLabel(party, t);
  const expiryDateAriaLabel = getPartyExpiryDateAriaLabel(party, t);
  const linkAriaLabel = getPartyLinkAriaLabel(party, t);

  return (
    <li
      sx={{
        display: 'flex',
        position: 'relative',
        my: [rem(8), 0, 0],
        borderRadius: [rem(8), rem(8), rem(8)],
        backgroundColor: colors.components.partyListItem.Background.Default,
        '&:hover': {
          backgroundColor: colors.components.partyListItem.Background.Hover,
        },
        '&:hover [data-election-type-badge], &:focus-within [data-election-type-badge]': {
          backgroundColor: colors.components.fixedTop.Background.Default,
        },
      }}
      className={className}
    >
      <Anchor
        ref={ref}
        sx={{
          display: 'block',
          width: '100%',
          textDecoration: 'none',
          '&:focus': {
            zIndex: 1,
          },
        }}
        prefetch={isFirstItem}
        href={getPartyHref(party, partyDetailsUrl)}
        aria-label={linkAriaLabel}
      >
        <div
          sx={{
            position: 'relative',
            minHeight: rem(200),
            px: rem(20),
            py: rem(20),
            width: '100%',
            display: 'block',
            color: colors.base.Text.PrimaryStatic,
          }}
        >
          <span
            sx={{
              position: 'absolute',
              top: rem(20),
              right: rem(20),
              display: 'inline-flex',
              justifyContent: 'flex-end',
            }}
          >
            <PartyElectionBadges badges={electionTypeBadges} align="right" />
          </span>
          <div>
            <Text
              variant={TextVariant.HeaderMd}
              as="p"
              sx={{
                color: colors.base.Text.PrimaryStatic,
                width: '100%',
                mb: rem(24),
                pr: rem(76),
                ...readableListTitleTextWrap,
              }}
            >
              {highlight && name ? (
                <Mark highlight={highlight}>{name}</Mark>
              ) : (
                name
              )}
            </Text>
          </div>
          <div
            sx={{
              textAlign: 'left',
              mb: rem(12),
            }}
          >
            <Text variant={TextVariant.HeaderXs} as="p" sx={headlineStyles}>
              {t('parties.partiesVotes')}
            </Text>
            <Text
              variant={TextVariant.BodyLg}
              as="span"
              aria-label={voteAriaLabel}
              sx={{
                color: colors.base.Text.PrimaryStatic,
              }}
            >
              {thousandsSeparators(voteCount)}
              {voteCount2 !== undefined
                ? ` / ${thousandsSeparators(voteCount2)}`
                : ''}
            </Text>
          </div>
          <div
            sx={{
              textAlign: 'left',
            }}
          >
            <Text variant={TextVariant.HeaderXs} as="p" sx={headlineStyles}>
              {t('parties.partiesExpirationDate')}
            </Text>
            <Text
              variant={TextVariant.BodyLg}
              as="span"
              aria-label={expiryDateAriaLabel}
              sx={{
                color: colors.base.Text.PrimaryStatic,
              }}
            >
              {visibleExpiryDates.join(' / ')}
            </Text>
          </div>
        </div>
      </Anchor>
    </li>
  );
}

export default PartyListItem;
